import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, GraduationCap, Sheet, Target } from "lucide-react";
import { fmtDateTime } from "@/lib/date";
import { StageNav, AdminSync } from "@/pages/Vetting";
import StepCard from "@/components/appointments/StepCard";
import StartHere from "@/components/appointments/StartHere";
import Podcasts from "@/components/appointments/Podcasts";
import { RunsText, ForYou } from "@/components/appointments/MessageCard";
import { PrimaryZone } from "@/components/vetting/ResourceCard";

const PHASES = [["before", "Before the appointment"], ["during", "During the appointment"], ["after", "Immediately after"], ["resources", "Resources & next step"]];

function Appointment({ stage }) {
  return (
    <div className="space-y-6">
      {stage.intro.map((x, i) => (
        <section key={i} className={x.type === "purpose" ? "rounded-2xl border-l-4 border-[#C9A961] bg-[#F7F5EF] p-4 sm:p-5" : "rounded-2xl border border-slate-200 bg-white p-4"} data-testid={`appt-intro-${i}`}>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#8A6D1F] font-semibold mb-1.5">
            {x.type === "purpose" && <Target className="w-3.5 h-3.5" />}{x.label}
          </div>
          <RunsText runs={x.body} />
          <ForYou runs={x.note} />
        </section>
      ))}
      {PHASES.filter(([k]) => stage.phases[k]?.length).map(([k, title], pi) => (
        <section key={k} data-testid={`appt-phase-${k}`}>
          <h3 className="font-serif text-xl text-[#0A1F44] mb-3">{title}</h3>
          <div className="space-y-5 border-l-2 border-[#C9A961]/30 ml-1 sm:ml-2 pl-0">
            {stage.phases[k].map((st, i) => <PrimaryZone key={i} on={(pi === 0 || k === "resources") && i < 2}><StepCard step={st} testid={`appt-${k}-step-${i}`} /></PrimaryZone>)}
          </div>
        </section>
      ))}
    </div>
  );
}

// Approved display labels; applied in the app so sheet syncs never overwrite them.
const LABELS = {
  "start-here": { name: "Start Here" },
  podcasts: { name: "Process Podcasts to Send PBO", nav: "Process Podcasts to Send PBO" },
};
const withLabels = (d) => ({ ...d, stages: d.stages.map((s) => ({ ...s, ...LABELS[s.id] })) });

export default function Appointments() {
  const { stageId } = useParams();
  const { user, isViewAs } = useAuth();
  const [data, setData] = useState(null);
  const load = () => api.get("/training/appointments").then((r) => setData(withLabels(r.data))).catch(() => toast.error("Couldn't load the guide"));
  useEffect(() => { load(); }, []);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [stageId]);
  if (!data) return <div className="text-sm text-slate-400 py-10 text-center">Loading guide…</div>;

  const idx = Math.max(0, data.stages.findIndex((s) => s.id === stageId));
  const stage = data.stages[idx];
  const prev = data.stages[idx - 1];
  const next = data.stages[idx + 1];
  return (
    <div className="space-y-6" data-testid="appointments-page">
      <section className="rounded-2xl bg-[#0A1F44] text-white p-5 sm:p-7 ring-1 ring-[#C9A961]/30">
        <Link to="/hub" className="inline-flex items-center min-h-[44px] text-sm text-white/80 hover:text-white">← Resources</Link>
        <h1 className="font-serif text-3xl sm:text-4xl mt-1">Process Appointments</h1>
        <p className="text-white/70 text-sm mt-1 max-w-2xl">Your step-by-step guide to preparing for appointments, communicating with your PBO and following through.</p>
        {user?.role === "admin" && !isViewAs && <a href={data.source_sheet} target="_blank" rel="noopener noreferrer" data-testid="appt-open-sheet"
           className="inline-flex items-center gap-1 text-[11px] text-[#C9A961] hover:text-white mt-3"><Sheet className="w-3.5 h-3.5" /> Open Google Sheet (admin)</a>}
      </section>
      <div className="grid gap-6 lg:grid-cols-[230px_1fr]">
        <StageNav stages={data.stages} current={stage.id} base="/appointments" chooseLabel="Choose an appointment" testid="appt" />
        <article className="space-y-5 min-w-0" data-testid={`appt-stage-${stage.id}`}>
          <header className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">{stage.code}</div>
              <h2 className="font-serif text-2xl sm:text-3xl text-[#0A1F44] mt-1" data-testid="appt-stage-title">{stage.name}</h2>
            </div>
            {stage.vetting_stage && (
              <Link to={`/vetting/${stage.vetting_stage}`} data-testid="appt-study-link"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#C9A961] text-[#0A1F44] text-xs font-semibold px-3 py-1.5 hover:bg-[#C9A961]/10">
                <GraduationCap className="w-4 h-4" /> Study this stage
              </Link>
            )}
          </header>
          {stage.type === "start" && <StartHere stage={stage} />}
          {stage.type === "podcasts" && <Podcasts stage={stage} />}
          {stage.type === "appointment" && <Appointment stage={stage} />}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-4">
            {prev ? <Button variant="outline" asChild className="rounded-full h-auto min-h-[44px] py-2 whitespace-normal text-left"><Link to={`/appointments/${prev.id}`} data-testid="appt-prev"><ChevronLeft className="w-4 h-4 mr-1" />{prev.nav}</Link></Button> : <span />}
            {next && <Button asChild className="rounded-full h-auto min-h-[44px] py-2 whitespace-normal text-left ml-auto bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90"><Link to={`/appointments/${next.id}`} data-testid="appt-next">{next.nav}<ChevronRight className="w-4 h-4 ml-1" /></Link></Button>}
          </div>
          <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-600 pt-2" data-testid="appt-synced-at">
            Content last synced from the master sheet {fmtDateTime(data.synced_at)} (Melbourne)
            {user?.can_sync_content && !isViewAs && <AdminSync slug="appointments" onDone={load} />}
          </div>
        </article>
      </div>
    </div>
  );
}
